import { type THeapPaymentMethodsJson } from "@features/checkout/types";
import type { PaymentMethodResponse } from "@interfaces/heap-api";
import { ClientFetchService } from "@lib/helpers/clientFetchService";

/**
 * Fetches the full HEAP payment-methods response for a cart (all providers plus the
 * Adyen client key), for the checkout payment step. Use fetchHeapAdyenPaymentMethods
 * when only the normalized Adyen method list is needed.
 */
export const fetchHeapPaymentMethods = async (cartId: string, shopperConversionId?: string) => {
    return ClientFetchService.post<
        { cartId: string; shopperConversionId?: string },
        PaymentMethodResponse
    >("/api/payment/heap/get-methods", { cartId, shopperConversionId });
};

/**
 * HEAP names the zero-amount method per market, so it has to be read from the
 * NoPay provider rather than assumed.
 */
export const findNoPayMethodName = (providers: THeapPaymentMethodsJson[] | undefined) => {
    const noPayProvider = providers?.find(
        (provider) => provider.PaymentProviderType === "NoPay"
    ) as Extract<THeapPaymentMethodsJson, { PaymentProviderType: "Adyen" | "NoPay" }> | undefined;

    return noPayProvider?.paymentMethods?.[0]?.name;
};

/**
 * Resolves the NoPay method name for a cart in one call, for callers that don't
 * already hold the payment-methods response.
 */
export const resolveNoPayMethodName = async (cartId: string) => {
    const response = await fetchHeapPaymentMethods(cartId);

    try {
        const providers = JSON.parse(response.paymentMethodsJson) as THeapPaymentMethodsJson[];
        return findNoPayMethodName(providers);
    } catch {
        return undefined;
    }
};
